import { t } from "i18next";

export const SURVEY_STATUS = {
    DRAFT: 1,
    OPEN: 2,
    CLOSED: 3,
    ARCHIVED: 4,
};

export const SURVEY_ACCESS = {
    PUBLIC: 1,
    AUTHENTICATED: 2,
    INVITATION: 3,
};

export const SURVEY_SUBJECT = {
    FREE: 1,
    ITEM: 2,
};

export const SURVEY_CHANNEL = {
    WEB: 1,
    PHONE: 2,
};

export const PHONE_DIAL = {
    MANUAL: 1,
    SYSTEM: 2,
};

export const PHONE_INTERVIEW = {
    OPERATOR: 1,
    IVR_DTMF: 2,
    IVR_VOICE: 3,
    MIXED: 4,
};

export const PHONE_ANSWER = {
    OPERATOR: 1,
    DTMF: 2,
    VOICE: 3,
};

export const PHONE_CALL = {
    QUEUED: 1,
    RINGING: 2,
    IN_PROGRESS: 3,
    COMPLETED: 4,
    NO_ANSWER: 5,
    BUSY: 6,
    FAILED: 7,
    VOICEMAIL: 8,
    WRONG_NUMBER: 9,
    REFUSED: 10,
    CALLBACK: 11,
    DNC: 12,
    PARTIAL: 13,
    LANGUAGE_BARRIER: 14,
    DISCONNECTED: 15,
};

export const CAMPAIGN_KIND = {
    LINK: 1,
    QR: 2,
    USERS: 3,
    CSV: 4,
    EMAIL: 5,
    SMS: 6,
    IN_APP: 7,
    PHONE: 8,
};

export const USER_AUDIENCE = {
    ALL: "all",
    ORG: "org",
    PUBLIC: "public",
    SPECIAL: "special",
};

const USERS_KIND_PREFIX = "users:";

export const CAMPAIGN_STATUS = {
    DRAFT: 1,
    SCHEDULED: 2,
    SENDING: 3,
    SENT: 4,
    CANCELLED: 5,
};

export const MESSAGE_TYPE = {
    ADMINISTRATIVE: 1,
    OPTIONAL: 2,
};

export const RECIPIENT_STATUS = {
    PENDING: 1,
    SENT: 2,
    FAILED: 3,
    STARTED: 4,
    COMPLETED: 5,
    SUPPRESSED: 6,
};

export const SENDER_CHANNEL = {
    EMAIL: 1,
    SMS: 2,
    IN_APP: 3,
};

/** Persian/UCS-2 SMS is billed per 70 characters. */
export const SMS_MESSAGE_MAX_CHARS = 70;

export const ORG_NAME_POSITION = {
    START: 1,
    END: 2,
};

export const composeCampaignMessageBody = (
    messageBody,
    organizationName,
    position = ORG_NAME_POSITION.END
) => {
    const body = String(messageBody || "").trim();
    const name = String(organizationName || "").trim();
    if (!name) return body;
    if (!body) return name;
    if (position === ORG_NAME_POSITION.START) {
        return `${name}\n${body}`;
    }
    return `${body}\n${name}`;
};

export const SURVEY_EXPORT_STATUS = {
    PENDING: 1,
    DONE: 2,
    FAILED: 3,
};

export const SURVEY_FORM_DISPLAY_SCROLL = "scroll";
export const SURVEY_FORM_DISPLAY_STEP = "step";

export const surveyFormDisplayOptions = () => [
    {
        value: SURVEY_FORM_DISPLAY_STEP,
        label: t("survey.form.display.step"),
    },
    {
        value: SURVEY_FORM_DISPLAY_SCROLL,
        label: t("survey.form.display.scroll"),
    },
];

export const surveyStatusOptions = () => [
    { value: SURVEY_STATUS.DRAFT, label: t("survey.status.draft") },
    { value: SURVEY_STATUS.OPEN, label: t("survey.status.open") },
    { value: SURVEY_STATUS.CLOSED, label: t("survey.status.closed") },
    { value: SURVEY_STATUS.ARCHIVED, label: t("survey.status.archived") },
];

export const surveyStatusLabel = (status) =>
    surveyStatusOptions().find((option) => option.value === status)?.label ||
    "";

export const accessModeOptions = () => [
    { value: SURVEY_ACCESS.PUBLIC, label: t("survey.access.public") },
    {
        value: SURVEY_ACCESS.AUTHENTICATED,
        label: t("survey.access.authenticated"),
    },
    { value: SURVEY_ACCESS.INVITATION, label: t("survey.access.invitation") },
];

export const accessModeLabel = (mode) =>
    accessModeOptions().find((option) => option.value === mode)?.label || "";

export const isLinkCampaign = (kind) => {
    const value = Number(kind);
    return value === CAMPAIGN_KIND.LINK || value === CAMPAIGN_KIND.QR;
};

export const isUsersCampaign = (kind) => Number(kind) === CAMPAIGN_KIND.USERS;

export const isPhoneKind = (kind) => Number(kind) === CAMPAIGN_KIND.PHONE;

export const CLUB_USER_AUDIENCES = [
    USER_AUDIENCE.PUBLIC,
    USER_AUDIENCE.ORG,
    USER_AUDIENCE.SPECIAL,
];

export const campaignAudienceKey = (campaign) => {
    if (!isUsersCampaign(campaign?.kind)) return "";
    return campaign.user_group_type || USER_AUDIENCE.ALL;
};

export const wizardDistributionCampaigns = (rows) =>
    (Array.isArray(rows) ? rows : []).filter(
        (row) => !isUsersCampaign(row.kind)
    );

export const resolveInSystemAudiences = (selected) => {
    const values = Array.isArray(selected) ? selected : [];
    if (values.includes(USER_AUDIENCE.ALL)) {
        return [USER_AUDIENCE.ALL];
    }
    return CLUB_USER_AUDIENCES.filter((audience) => values.includes(audience));
};

export const findUsersCampaignForAudience = (rows, audience, surveyId) => {
    const matches = (Array.isArray(rows) ? rows : []).filter(
        (row) => campaignAudienceKey(row) === audience
    );
    const bound = matches.filter((row) =>
        campaignBelongsToSurvey(row, surveyId)
    );
    const unbound = matches.filter(campaignIsUnbound);
    return (
        bound.find(campaignIsSendable) ||
        unbound.find(campaignIsSendable) ||
        null
    );
};

export const campaignKindSelectValue = (kind, userGroupType) => {
    if (kind === CAMPAIGN_KIND.USERS) {
        return `${USERS_KIND_PREFIX}${userGroupType || USER_AUDIENCE.ALL}`;
    }
    return kind;
};

export const parseCampaignKindSelect = (value) => {
    if (typeof value === "string" && value.startsWith(USERS_KIND_PREFIX)) {
        const audience = value.slice(USERS_KIND_PREFIX.length);
        return {
            kind: CAMPAIGN_KIND.USERS,
            user_group_type:
                audience === USER_AUDIENCE.ALL ? "" : audience,
        };
    }
    return { kind: value, user_group_type: "" };
};

export const withPersonalScope = (personal, params = {}) =>
    personal ? { ...params, scope: "personal" } : params;

export const withPersonalPayload = (personal, payload) =>
    personal ? { ...payload, personal: true } : payload;

export const campaignKindOptions = (
    currentKind,
    { linkOnly = false, phoneSurvey = false, allowPhone = false } = {}
) => {
    const linkValue = isLinkCampaign(currentKind)
        ? currentKind
        : CAMPAIGN_KIND.LINK;
    const link = { value: linkValue, label: t("survey.campaign.kind.link") };
    if (linkOnly) {
        return [link];
    }
    const phone = {
        value: CAMPAIGN_KIND.PHONE,
        label: t("survey.campaign.kind.phone"),
    };
    if (phoneSurvey) {
        return [phone];
    }
    const digital = [
        link,
        { value: CAMPAIGN_KIND.CSV, label: t("survey.campaign.kind.csv") },
        { value: CAMPAIGN_KIND.EMAIL, label: t("survey.campaign.kind.email") },
        { value: CAMPAIGN_KIND.SMS, label: t("survey.campaign.kind.sms") },
        { value: CAMPAIGN_KIND.IN_APP, label: t("survey.campaign.kind.in_app") },
    ];
    if (allowPhone || isPhoneKind(currentKind)) {
        digital.push(phone);
    }
    return digital;
};

export const campaignKindLabel = (kind, userGroupType) => {
    const value = Number(kind);
    if (isLinkCampaign(value)) {
        return t("survey.campaign.kind.link");
    }
    if (value === CAMPAIGN_KIND.USERS) {
        const key = {
            [USER_AUDIENCE.ORG]: "survey.campaign.kind.users.org",
            [USER_AUDIENCE.PUBLIC]: "survey.campaign.kind.users.public",
            [USER_AUDIENCE.SPECIAL]: "survey.campaign.kind.users.special",
        }[userGroupType];
        return t(key || "survey.campaign.kind.users.all");
    }
    if (value === CAMPAIGN_KIND.PHONE) {
        return t("survey.campaign.kind.phone");
    }
    return (
        campaignKindOptions().find((option) => option.value === value)?.label ||
        ""
    );
};

export const campaignDeliveryLabel = (kind) =>
    isPhoneKind(kind)
        ? t("survey.campaign.delivery.phone")
        : t("survey.campaign.delivery.digital");

export const campaignStatusLabel = (status) =>
    ({
        [CAMPAIGN_STATUS.DRAFT]: t("survey.campaign.status.draft"),
        [CAMPAIGN_STATUS.SCHEDULED]: t("survey.campaign.status.scheduled"),
        [CAMPAIGN_STATUS.SENDING]: t("survey.campaign.status.sending"),
        [CAMPAIGN_STATUS.SENT]: t("survey.campaign.status.sent"),
        [CAMPAIGN_STATUS.CANCELLED]: t("survey.campaign.status.cancelled"),
    }[status] || "");

export const campaignIsEditable = (campaign) => {
    const status = campaign?.status;
    return (
        !campaign?.id ||
        status === CAMPAIGN_STATUS.DRAFT ||
        status === CAMPAIGN_STATUS.SCHEDULED ||
        status === CAMPAIGN_STATUS.SENT
    );
};

export const campaignIsSendable = (campaign) =>
    campaign?.status === CAMPAIGN_STATUS.DRAFT ||
    campaign?.status === CAMPAIGN_STATUS.SCHEDULED ||
    campaign?.status === CAMPAIGN_STATUS.SENT;

export const sameSurveyId = (left, right) =>
    left != null && right != null && String(left) === String(right);

export const campaignBelongsToSurvey = (campaign, surveyId) =>
    sameSurveyId(campaign?.survey, surveyId);

export const campaignIsUnbound = (campaign) =>
    !campaign?.survey && !campaign?.content;

export const campaignsForSurveyWizard = (
    rows,
    { surveyId, linkOnly = false } = {}
) => {
    const filtered = (Array.isArray(rows) ? rows : []).filter((row) => {
        if (row?.content) return false;
        if (linkOnly && !isLinkCampaign(row.kind)) return false;
        return true;
    });
    const rank = (row) => {
        if (campaignBelongsToSurvey(row, surveyId)) return 0;
        if (campaignIsUnbound(row)) return 1;
        return 2;
    };
    return [...filtered].sort((a, b) => {
        const byRank = rank(a) - rank(b);
        if (byRank !== 0) return byRank;
        return (b.id || 0) - (a.id || 0);
    });
};

export const pickWizardCampaignId = (rows, preferredId, surveyId) => {
    if (preferredId && rows.some((row) => row.id === preferredId)) {
        return preferredId;
    }
    const bound = rows.filter((row) => campaignBelongsToSurvey(row, surveyId));
    const unbound = rows.filter(campaignIsUnbound);
    const sendableBound = bound.find(campaignIsSendable);
    if (sendableBound) return sendableBound.id;
    const sendableUnbound = unbound.find(campaignIsSendable);
    if (sendableUnbound) return sendableUnbound.id;
    if (bound[0]) return bound[0].id;
    if (unbound[0]) return unbound[0].id;
    const sendable = rows.find(campaignIsSendable);
    return sendable?.id || rows[0]?.id || null;
};

export const campaignIsCancellable = (campaign) =>
    campaign?.status === CAMPAIGN_STATUS.DRAFT ||
    campaign?.status === CAMPAIGN_STATUS.SCHEDULED;

export const campaignIsDeletable = (campaign) =>
    Boolean(campaign?.id) && campaign.status !== CAMPAIGN_STATUS.SENDING;

export const messageTypeOptions = () => [
    {
        value: MESSAGE_TYPE.ADMINISTRATIVE,
        label: t("survey.campaign.message.type.administrative"),
    },
    {
        value: MESSAGE_TYPE.OPTIONAL,
        label: t("survey.campaign.message.type.optional"),
    },
];

export const organizationNamePositionOptions = () => [
    {
        value: ORG_NAME_POSITION.START,
        label: t("survey.campaign.organization.position.start"),
    },
    {
        value: ORG_NAME_POSITION.END,
        label: t("survey.campaign.organization.position.end"),
    },
];

export const recipientStatusLabel = (status) =>
    ({
        [RECIPIENT_STATUS.PENDING]: t("survey.recipient.status.pending"),
        [RECIPIENT_STATUS.SENT]: t("survey.recipient.status.sent"),
        [RECIPIENT_STATUS.FAILED]: t("survey.recipient.status.failed"),
        [RECIPIENT_STATUS.STARTED]: t("survey.recipient.status.started"),
        [RECIPIENT_STATUS.COMPLETED]: t("survey.recipient.status.completed"),
        [RECIPIENT_STATUS.SUPPRESSED]: t(
            "survey.recipient.status.suppressed"
        ),
    }[status] || "");

export const senderChannelOptions = () => [
    {
        value: SENDER_CHANNEL.EMAIL,
        label: t("survey.sender.profile.channel.email"),
    },
    {
        value: SENDER_CHANNEL.SMS,
        label: t("survey.sender.profile.channel.sms"),
    },
    {
        value: SENDER_CHANNEL.IN_APP,
        label: t("survey.sender.profile.channel.in_app"),
    },
];

/** Formats API datetime strings to date + HH:MM (drops seconds/offset). */
export const formatSurveyDateTime = (value) => {
    if (!value) return "—";
    const match = String(value).match(
        /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/
    );
    if (match) return `${match[1]} ${match[2]}`;
    return String(value);
};

export const surveySubjectLabel = (survey) =>
    survey?.item_name || survey?.item_type_name || survey?.subject_title || "";

export const SURVEY_TEMPLATE_CATEGORY_ORDER = [
    "customer_metric",
    "service_quality",
    "employee",
    "industry",
];

export const surveyTemplateItemCount = (template) =>
    Array.isArray(template?.structure?.criteria)
        ? template.structure.criteria.length
        : 0;

export const groupSurveyTemplates = (templates) => {
    const groups = [];
    const indexByCategory = {};
    (Array.isArray(templates) ? templates : []).forEach((template) => {
        const category = template.category || "other";
        if (indexByCategory[category] === undefined) {
            indexByCategory[category] = groups.length;
            groups.push({ category, templates: [] });
        }
        groups[indexByCategory[category]].templates.push(template);
    });
    const rank = (category) => {
        const index = SURVEY_TEMPLATE_CATEGORY_ORDER.indexOf(category);
        return index === -1 ? SURVEY_TEMPLATE_CATEGORY_ORDER.length : index;
    };
    return groups.sort((left, right) => rank(left.category) - rank(right.category));
};

export const flattenItemTypeOptions = (nodes, prefix = "") => {
    const options = [];
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        const label = prefix ? `${prefix} / ${node.name}` : node.name;
        options.push({ value: node.id, label });
        if (node.children?.length) {
            options.push(...flattenItemTypeOptions(node.children, label));
        }
    });
    return options;
};

export const surveyChannelOptions = () => [
    { value: SURVEY_CHANNEL.WEB, label: t("survey.channel.web") },
    { value: SURVEY_CHANNEL.PHONE, label: t("survey.channel.phone") },
];

export const surveyChannelLabel = (channel) =>
    surveyChannelOptions().find((option) => option.value === channel)?.label ||
    "";

export const defaultPhoneSettings = () => ({
    dial_mode: PHONE_DIAL.MANUAL,
    interview_mode: PHONE_INTERVIEW.OPERATOR,
    max_attempts: 3,
    retry_interval_minutes: 60,
    calling_window_start: "09:00",
    calling_window_end: "20:00",
    record_calls: false,
    timeout_seconds: 8,
    max_invalid_retries: 2,
    opt_out_digit: "9",
    greeting_text: "",
    goodbye_text: "",
    target_completes: null,
});

export const phoneDialOptions = () => [
    { value: PHONE_DIAL.MANUAL, label: t("survey.phone.dial.manual") },
    { value: PHONE_DIAL.SYSTEM, label: t("survey.phone.dial.system") },
];

export const phoneInterviewOptions = () => [
    {
        value: PHONE_INTERVIEW.OPERATOR,
        label: t("survey.phone.interview.operator"),
    },
    {
        value: PHONE_INTERVIEW.IVR_DTMF,
        label: t("survey.phone.interview.ivr_dtmf"),
    },
    {
        value: PHONE_INTERVIEW.IVR_VOICE,
        label: t("survey.phone.interview.ivr_voice"),
    },
    { value: PHONE_INTERVIEW.MIXED, label: t("survey.phone.interview.mixed") },
];

export const phoneAnswerOptions = () => [
    { value: PHONE_ANSWER.OPERATOR, label: t("survey.phone.answer.operator") },
    { value: PHONE_ANSWER.DTMF, label: t("survey.phone.answer.dtmf") },
    { value: PHONE_ANSWER.VOICE, label: t("survey.phone.answer.voice") },
];

export const phoneCallStatusLabel = (status) =>
    ({
        [PHONE_CALL.QUEUED]: t("survey.phone.call.queued"),
        [PHONE_CALL.RINGING]: t("survey.phone.call.ringing"),
        [PHONE_CALL.IN_PROGRESS]: t("survey.phone.call.in_progress"),
        [PHONE_CALL.COMPLETED]: t("survey.phone.call.completed"),
        [PHONE_CALL.NO_ANSWER]: t("survey.phone.call.no_answer"),
        [PHONE_CALL.BUSY]: t("survey.phone.call.busy"),
        [PHONE_CALL.FAILED]: t("survey.phone.call.failed"),
        [PHONE_CALL.VOICEMAIL]: t("survey.phone.call.voicemail"),
        [PHONE_CALL.WRONG_NUMBER]: t("survey.phone.call.wrong_number"),
        [PHONE_CALL.REFUSED]: t("survey.phone.call.refused"),
        [PHONE_CALL.CALLBACK]: t("survey.phone.call.callback"),
        [PHONE_CALL.DNC]: t("survey.phone.call.dnc"),
        [PHONE_CALL.PARTIAL]: t("survey.phone.call.partial"),
        [PHONE_CALL.LANGUAGE_BARRIER]: t("survey.phone.call.language_barrier"),
        [PHONE_CALL.DISCONNECTED]: t("survey.phone.call.disconnected"),
    }[status] || "");

export const phoneDispositionOptions = () => [
    { value: PHONE_CALL.COMPLETED, label: t("survey.phone.call.completed") },
    { value: PHONE_CALL.PARTIAL, label: t("survey.phone.call.partial") },
    { value: PHONE_CALL.NO_ANSWER, label: t("survey.phone.call.no_answer") },
    { value: PHONE_CALL.BUSY, label: t("survey.phone.call.busy") },
    { value: PHONE_CALL.VOICEMAIL, label: t("survey.phone.call.voicemail") },
    { value: PHONE_CALL.CALLBACK, label: t("survey.phone.call.callback") },
    { value: PHONE_CALL.REFUSED, label: t("survey.phone.call.refused") },
    { value: PHONE_CALL.WRONG_NUMBER, label: t("survey.phone.call.wrong_number") },
    { value: PHONE_CALL.DISCONNECTED, label: t("survey.phone.call.disconnected") },
    {
        value: PHONE_CALL.LANGUAGE_BARRIER,
        label: t("survey.phone.call.language_barrier"),
    },
    { value: PHONE_CALL.DNC, label: t("survey.phone.call.dnc") },
    { value: PHONE_CALL.FAILED, label: t("survey.phone.call.failed") },
];

export const isPhoneSurvey = (survey) =>
    Number(survey?.channel) === SURVEY_CHANNEL.PHONE;

export const isPhoneCampaign = (campaign) => isPhoneKind(campaign?.kind);

export const campaignFitsSurvey = (campaign, survey) => {
    if (!survey) return true;
    return isPhoneSurvey(survey) === isPhoneCampaign(campaign);
};

